import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Button,
    IconButton,
    Card
} from "@material-tailwind/react";
import Cookies from 'js-cookie';

const BOARD_WIDTH = 10;
const BOARD_HEIGHT = 20;
const EMPTY_CELL = 0;
const GAME_SPEED = 160; // Milliseconds between automatic downward moves

const SHAPES = [
    [[1, 1, 1, 1]],         // I
    [[1, 1], [1, 1]],       // O
    [[1, 1, 1], [0, 1, 0]], // T
    [[1, 1, 1], [1, 0, 0]], // L
    [[1, 1, 1], [0, 0, 1]], // J
    [[1, 1, 0], [0, 1, 1]], // S
    [[0, 1, 1], [1, 1, 0]]  // Z
];

const COLORS = ['bg-red-300', 'bg-blue-300', 'bg-green-300', 'bg-indigo-300', 'bg-purple-300', 'bg-orange-300', 'bg-pink-300'];

const TetrisGame = () => {
    const [board, setBoard] = useState(Array(BOARD_HEIGHT).fill().map(() => Array(BOARD_WIDTH).fill(EMPTY_CELL)));
    const [currentPiece, setCurrentPiece] = useState(null);
    const [highScore, setHighScore] = useState(0);
    const [gameOver, setGameOver] = useState(false);
    const [score, setScore] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const [animatingLines, setAnimatingLines] = useState([]);
    const [placedPiece, setPlacedPiece] = useState(null);
    const gameLoopRef = useRef(null);

    useEffect(() => {
        const savedHighScore = Cookies.get('tetrisHighScore');
        if (savedHighScore) {
            setHighScore(parseInt(savedHighScore, 10));
        }
    }, []);

    useEffect(() => {
        if (score > highScore) {
            setHighScore(score);
            Cookies.set('tetrisHighScore', score, { expires: 365 });
        }
    }, [score, highScore]);

    const createNewPiece = useCallback(() => {
        const shapeIndex = Math.floor(Math.random() * SHAPES.length);
        const shape = SHAPES[shapeIndex];
        const color = COLORS[shapeIndex];
        const x = Math.floor((BOARD_WIDTH - shape[0].length) / 2);
        const y = 0;
        return { shape, color, x, y };
    }, []);

    const isValidMove = useCallback((piece, boardToCheck, ignoreTop = false) => {
        for (let y = 0; y < piece.shape.length; y++) {
            for (let x = 0; x < piece.shape[y].length; x++) {
                if (piece.shape[y][x]) {
                    const newX = piece.x + x;
                    const newY = piece.y + y;
                    if (newX < 0 || newX >= BOARD_WIDTH || newY >= BOARD_HEIGHT ||
                        (!ignoreTop && newY < 0) ||
                        (newY >= 0 && boardToCheck[newY][newX] !== EMPTY_CELL)) {
                        return false;
                    }
                }
            }
        }
        return true;
    }, []);

    const placePiece = useCallback((piece) => {
        const newBoard = [...board];
        for (let y = 0; y < piece.shape.length; y++) {
            for (let x = 0; x < piece.shape[y].length; x++) {
                if (piece.shape[y][x] && piece.y + y >= 0) {
                    newBoard[piece.y + y][piece.x + x] = piece.color;
                }
            }
        }
        setBoard(newBoard);
        setPlacedPiece(piece);
        setTimeout(() => setPlacedPiece(null), 300);
    }, [board]);

    const clearLines = useCallback(() => {
        let newBoard = [...board];
        let linesToClear = [];

        for (let y = BOARD_HEIGHT - 1; y >= 0; y--) {
            if (newBoard[y].every(cell => cell !== EMPTY_CELL)) {
                linesToClear.push(y);
            }
        }

        if (linesToClear.length > 0) {
            setAnimatingLines(linesToClear);
            setTimeout(() => {
                newBoard = newBoard.filter((_, index) => !linesToClear.includes(index));
                while (newBoard.length < BOARD_HEIGHT) {
                    newBoard.unshift(Array(BOARD_WIDTH).fill(EMPTY_CELL));
                }
                setBoard(newBoard);
                setScore(prevScore => prevScore + linesToClear.length * 100);
                setAnimatingLines([]);
            }, 300);  // Shortened animation time for faster gameplay
        }
    }, [board]);

    const moveDown = useCallback((steps = 1) => {
        if (!currentPiece || gameOver || isPaused) return;
        let newPiece = { ...currentPiece };
        let moved = 0;
        while (moved < steps) {
            const nextPosition = { ...newPiece, y: newPiece.y + 1 };
            if (isValidMove(nextPosition, board)) {
                newPiece = nextPosition;
                moved++;
            } else {
                break;
            }
        }
        if (moved > 0) {
            setCurrentPiece(newPiece);
        } else {
            placePiece(currentPiece);
            clearLines();
            const nextPiece = createNewPiece();
            if (isValidMove(nextPiece, board, true)) {
                setCurrentPiece(nextPiece);
            } else {
                // Place the last piece before the game ends
                placePiece(nextPiece);
                setCurrentPiece(null);
                setGameOver(true);
            }
        }
    }, [currentPiece, board, isValidMove, placePiece, createNewPiece, gameOver, isPaused, clearLines]);

    const movePiece = useCallback((dx) => {
        if (!currentPiece || gameOver || isPaused) return;
        const newPiece = { ...currentPiece, x: currentPiece.x + dx };
        if (isValidMove(newPiece, board)) {
            setCurrentPiece(newPiece);
        }
    }, [currentPiece, board, isValidMove, gameOver, isPaused]);

    const rotatePiece = useCallback(() => {
        if (!currentPiece || gameOver || isPaused) return;
        const newShape = currentPiece.shape[0].map((_, index) =>
            currentPiece.shape.map(row => row[index]).reverse()
        );
        const newPiece = { ...currentPiece, shape: newShape };
        if (isValidMove(newPiece, board)) {
            setCurrentPiece(newPiece);
        }
    }, [currentPiece, board, isValidMove, gameOver, isPaused]);

    const handleKeyPress = useCallback((e) => {
        if (e.key === 'ArrowLeft') movePiece(-1);
        if (e.key === 'ArrowRight') movePiece(1);
        if (e.key === 'ArrowDown') moveDown();
        if (e.key === 'ArrowUp') rotatePiece();
        if (e.key === ' ') setIsPaused(prev => !prev);
    }, [movePiece, moveDown, rotatePiece]);

    const initGame = useCallback(() => {
        setBoard(Array(BOARD_HEIGHT).fill().map(() => Array(BOARD_WIDTH).fill(EMPTY_CELL)));
        const newPiece = createNewPiece();
        setCurrentPiece(newPiece);
        setGameOver(false);
        setScore(0);
        setIsPaused(false);
        setAnimatingLines([]);
        setPlacedPiece(null);
    }, [createNewPiece]);

    const resetGame = () => {
        initGame();
    };

    useEffect(() => {
        initGame();
    }, [initGame]);

    useEffect(() => {
        const gameLoop = () => {
            if (!isPaused && !gameOver) {
                moveDown(1);
            }
        };

        const gameLoopInterval = setInterval(gameLoop, GAME_SPEED);
        gameLoopRef.current = gameLoopInterval;

        window.addEventListener('keydown', handleKeyPress);

        return () => {
            clearInterval(gameLoopInterval);
            window.removeEventListener('keydown', handleKeyPress);
        };
    }, [moveDown, handleKeyPress, isPaused, gameOver]);

    const renderBoard = () => {
        const boardToRender = board.map(row => [...row]);
        if (currentPiece) {
            for (let y = 0; y < currentPiece.shape.length; y++) {
                for (let x = 0; x < currentPiece.shape[y].length; x++) {
                    if (currentPiece.shape[y][x] && currentPiece.y + y >= 0) {
                        boardToRender[currentPiece.y + y][currentPiece.x + x] = currentPiece.color;
                    }
                }
            }
        }

        return boardToRender.map((row, y) => (
            <div key={y} className="flex">
                {row.map((cell, x) => (
                    <div
                        key={x}
                        className={`
                            w-5 h-5 border border-gray-200 
                            ${cell || 'bg-gray-50'}
                            ${animatingLines.includes(y) ? 'animate-flash' : ''}
                            ${placedPiece &&
                                x >= placedPiece.x &&
                                x < placedPiece.x + placedPiece.shape[0].length &&
                                y >= placedPiece.y &&
                                y < placedPiece.y + placedPiece.shape.length &&
                                placedPiece.shape[y - placedPiece.y][x - placedPiece.x]
                                ? 'animate-placed' : ''}
                        `}
                    ></div>
                ))}
            </div>
        ));
    };

    return (
        <div className="flex flex-col items-center justify-center p-4 rounded-lg relative">
            <style jsx global>{`
                @keyframes flash {
                    0%, 50% { opacity: 1; }
                    25%, 75% { opacity: 0; }
                    100% { opacity: 1; }
                }
                .animate-flash {
                    animation: flash 0.3s linear;
                }
                @keyframes placed {
                    0% { transform: scale(1.2); }
                    100% { transform: scale(1); }
                }
                .animate-placed {
                    animation: placed 0.3s ease-out;
                }
            `}</style>

            <div className='flex items-center gap-8 justify-between mb-4'>
                <p className="text-xl font-semibold">Score: {score}</p>
                <p className="text-xl font-semibold">High Score: {highScore}</p>
            </div>
            <div className="border-2 bg-white border-gray-400 p-1 mb-4 relative rounded-md">
                {gameOver && (
                    <div className="absolute inset-0 bg-black bg-opacity-75 flex flex-col items-center justify-center z-10">
                        <p className="text-3xl font-bold text-white mb-4">Game Over</p>
                        <Button
                            color="green"
                            className=" text-white font-bold py-2 px-4 rounded"
                            onClick={resetGame}
                        >
                            Play Again
                        </Button>
                    </div>
                )}
                {isPaused && !gameOver && (
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex flex-col items-center justify-center z-10">
                        <p className="text-3xl font-bold text-white mb-4">Paused</p>
                    </div>
                )}
                {renderBoard()}
            </div>

            <div className='flex flex-col items-center justify-between gap-2'>
                <div className='flex gap-2'>
                    <Button
                        color="white"
                        size='xl'
                        variant='gradient'
                        disabled
                        className="opacity-0 bg-white text-white"

                    >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 12c0-1.232-.046-2.453-.138-3.662a4.006 4.006 0 0 0-3.7-3.7 48.678 48.678 0 0 0-7.324 0 4.006 4.006 0 0 0-3.7 3.7c-.017.22-.032.441-.046.662M19.5 12l3-3m-3 3-3-3m-12 3c0 1.232.046 2.453.138 3.662a4.006 4.006 0 0 0 3.7 3.7 48.656 48.656 0 0 0 7.324 0 4.006 4.006 0 0 0 3.7-3.7c.017-.22.032-.441.046-.662M4.5 12l3 3m-3-3-3 3" />
                        </svg>

                    </Button>
                    <Button

                        className="shadow-none"
                        size='xl'
                        color='blue-gray'
                        variant=''
                        onClick={rotatePiece}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 12c0-1.232-.046-2.453-.138-3.662a4.006 4.006 0 0 0-3.7-3.7 48.678 48.678 0 0 0-7.324 0 4.006 4.006 0 0 0-3.7 3.7c-.017.22-.032.441-.046.662M19.5 12l3-3m-3 3-3-3m-12 3c0 1.232.046 2.453.138 3.662a4.006 4.006 0 0 0 3.7 3.7 48.656 48.656 0 0 0 7.324 0 4.006 4.006 0 0 0 3.7-3.7c.017-.22.032-.441.046-.662M4.5 12l3 3m-3-3-3 3" />
                        </svg>

                    </Button>
                    <Button
                        color="white"
                        size='xl'
                        variant='gradient'
                        disabled
                        className="opacity-0 bg-white text-white"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 12c0-1.232-.046-2.453-.138-3.662a4.006 4.006 0 0 0-3.7-3.7 48.678 48.678 0 0 0-7.324 0 4.006 4.006 0 0 0-3.7 3.7c-.017.22-.032.441-.046.662M19.5 12l3-3m-3 3-3-3m-12 3c0 1.232.046 2.453.138 3.662a4.006 4.006 0 0 0 3.7 3.7 48.656 48.656 0 0 0 7.324 0 4.006 4.006 0 0 0 3.7-3.7c.017-.22.032-.441.046-.662M4.5 12l3 3m-3-3-3 3" />
                        </svg>

                    </Button>
                </div>

                <div className="flex justify-center gap-2">
                    <Button
                        className=""
                        size='xl'
                        color='blue-gray'
                        variant='outlined'
                        onClick={() => movePiece(-1)}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
                        </svg>
                    </Button>

                    <Button
                        className=""
                        size='xl'
                        color='blue-gray'
                        variant='outlined'
                        onClick={() => moveDown()}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6">
                            <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                        </svg>
                    </Button>



                    <Button
                        className=""
                        size='xl'
                        color='blue-gray'
                        variant='outlined'
                        onClick={() => movePiece(1)}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6">
                            <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
                        </svg>
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default TetrisGame;